/**
 * useUiPreference — read/write a server-backed UI preference (the
 * ``ui_preference`` store) with a localStorage write-through cache.
 *
 * Reads the cached value synchronously for an instant first paint, then
 * fetches the authoritative server value on mount and reconciles. Writes
 * update local state + cache immediately (optimistic) and PUT to the server
 * in the background; an offline/failed PUT leaves the cache as the local
 * source of truth. Phase 1 targets the shared (venue-wide) row.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api/client";

const CACHE_PREFIX = "uiPref:";

function readCache<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (raw != null) return JSON.parse(raw) as T;
  } catch {
    /* corrupt/unavailable cache — fall through to default */
  }
  return fallback;
}

function writeCache<T>(key: string, value: T): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private mode — server is still the durable store */
  }
}

export function useUiPreference<T>(
  key: string,
  defaultValue: T,
): { value: T; setValue: (v: T) => void; loading: boolean } {
  const [value, setValueState] = useState<T>(() => readCache(key, defaultValue));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getUiPref<T>(key)
      .then((r) => {
        if (cancelled) return;
        if (r.value !== null && r.value !== undefined) {
          setValueState(r.value);
          writeCache(key, r.value);
        }
      })
      .catch(() => {
        /* keep the cached/default value */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const setValue = useCallback(
    (v: T) => {
      setValueState(v);
      writeCache(key, v);
      void api.putUiPref<T>(key, v).catch(() => {
        /* offline — the cache holds the change until next successful write */
      });
    },
    [key],
  );

  return { value, setValue, loading };
}
